import { useState } from 'react';
import { useScrollReveal, useScrollParallax } from '@/hooks/useScrollReveal';

export function generateWhatsAppLink(
  checkin: string,
  checkout: string,
  guests: string,
  room: string,
  name?: string,
  phone?: string,
  note?: string,
) {
  let message = 'Merhaba, İlkim Butik Otel için fiyat ve müsaitlik bilgisi almak istiyorum.';
  if (name) message += `\n\nAd Soyad: ${name}`;
  if (phone) message += `\nTelefon: ${phone}`;
  message += `\nGiriş tarihi: ${checkin}`;
  message += `\nÇıkış tarihi: ${checkout}`;
  message += `\nKişi sayısı: ${guests}`;
  message += `\nOda tercihi: ${room}`;
  if (note) message += `\nNot: ${note}`;
  message += '\n\nSeçtiğim tarihler için fiyat teklifi alabilir miyim?';

  return 'https://wa.me/905395851199?text=' + encodeURIComponent(message);
}

export function generateSimpleWhatsAppLink(roomType?: string) {
  let message = 'Merhaba, İlkim Butik Otel için fiyat ve müsaitlik bilgisi almak istiyorum.';
  if (roomType) {
    message += `\n\nOda tercihi: ${roomType}`;
  }
  message += '\n\nFiyat teklifi alabilir miyim?';
  return 'https://wa.me/905395851199?text=' + encodeURIComponent(message);
}

export default function HeroSection() {
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [guests, setGuests] = useState('2');
  const [room, setRoom] = useState('');
  const [error, setError] = useState('');

  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal({ threshold: 0 });
  const { ref: parallaxRef, offset } = useScrollParallax(0.15);

  const handleWhatsApp = () => {
    if (!checkin || !checkout || !guests || !room) {
      setError('Lütfen giriş tarihi, çıkış tarihi, kişi sayısı ve oda tercihi seçin.');
      return;
    }
    setError('');
    const link = generateWhatsAppLink(checkin, checkout, guests, room);
    window.open(link, '_blank');
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <section id="hero" ref={heroRef} className="relative min-h-screen w-full flex flex-col justify-end overflow-hidden">
      {/* Floating decorative petals - matching logo's pink flower accents */}
      <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
        <div className="absolute top-[15%] left-[8%] w-3 h-3 rounded-full bg-pink-400/30 animate-float-petal" style={{ animationDuration: '3s', animationDelay: '0s' }} />
        <div className="absolute top-[25%] right-[12%] w-2 h-2 rounded-full bg-pink-400/20 animate-float-petal" style={{ animationDuration: '4s', animationDelay: '1s' }} />
        <div className="absolute top-[60%] left-[15%] w-2.5 h-2.5 rounded-full bg-pink-400/25 animate-float-petal" style={{ animationDuration: '5s', animationDelay: '2s' }} />
        <div className="absolute top-[40%] right-[20%] w-3 h-3 rounded-full bg-pink-400/20 animate-float-petal" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }} />
        <div className="absolute top-[75%] left-[25%] w-2 h-2 rounded-full bg-pink-400/30 animate-float-petal" style={{ animationDuration: '4.5s', animationDelay: '1.5s' }} />
        <div className="absolute top-[10%] right-[30%] w-2 h-2 rounded-full bg-pink-400/20 animate-float-petal" style={{ animationDuration: '3.2s', animationDelay: '2.5s' }} />
      </div>

      {/* Background Image with Parallax */}
      <div ref={parallaxRef} className="absolute inset-0" style={{ transform: `translateY(${offset}px)` }}>
        <img
          src="https://readdy.ai/api/search-image?query=Premium%20boutique%20hotel%20garden%20with%20turquoise%20swimming%20pool%20surrounded%20by%20lush%20green%20palm%20trees%20and%20olive%20trees%2C%20Mediterranean%20coastal%20view%20with%20turquoise%20sea%20in%20background%2C%20warm%20golden%20summer%20light%2C%20white%20stone%20architecture%2C%20serene%20peaceful%20atmosphere%2C%20editorial%20travel%20photography%2C%20high%20quality%20luxury%20resort%20aesthetic%20with%20natural%20tones&width=1600&height=900&seq=hero-ilkim-01&orientation=landscape"
          alt="İlkim Butik Otel Bahçe ve Havuz"
          className="w-full h-full object-cover"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/60" />
      </div>

      {/* Content */}
      <div className={`relative z-10 w-full px-4 md:px-8 lg:px-12 pb-6 md:pb-12 pt-32 md:pt-40 transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Headline */}
        <div className="max-w-4xl mb-6 md:mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
            Marmaris Orhaniye'de<br />
            Denize 70 Metre<br />
            Huzurlu Butik Otel Tatili
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-2xl leading-relaxed">
            Kız Kumu Plajı'na yürüme mesafesinde, doğa içinde, açık havuzlu ve serpme köy kahvaltılı butik otel deneyimi.
          </p>
        </div>

        {/* Trust Badges */}
        <div className={`flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-8 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {[
            { icon: 'ri-water-flash-line', text: 'Denize 70 metre' },
            { icon: 'ri-walk-line', text: "Kız Kumu'na yürüme mesafesi" },
            { icon: 'ri-drop-line', text: 'Açık yüzme havuzu' },
            { icon: 'ri-restaurant-line', text: 'Serpme köy kahvaltısı' },
            { icon: 'ri-home-smile-line', text: '1+1 aile odası ve standart oda' },
          ].map((badge, i) => (
            <div
              key={badge.text}
              className={`flex items-center gap-2 px-3 py-2 bg-white/15 backdrop-blur-sm rounded-full text-white text-xs md:text-sm font-medium transition-all duration-500 hover:bg-white/25 hover:scale-105`}
              style={{ transitionDelay: `${400 + i * 100}ms` }}
            >
              <i className={badge.icon} />
              {badge.text}
            </div>
          ))}
        </div>

        {/* Booking Box */}
        <div className={`bg-background-50/95 backdrop-blur-md rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 max-w-5xl shadow-lg transition-all duration-700 delay-500 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
            {/* Check-in */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Giriş Tarihi
              </label>
              <input
                type="date"
                value={checkin}
                min={today}
                onChange={(e) => {
                  setCheckin(e.target.value);
                  if (checkout && e.target.value >= checkout) {
                    setCheckout('');
                  }
                  setError('');
                }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
            </div>

            {/* Check-out */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Çıkış Tarihi
              </label>
              <input
                type="date"
                value={checkout}
                min={checkin || today}
                onChange={(e) => {
                  setCheckout(e.target.value);
                  setError('');
                }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
            </div>

            {/* Guests */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Kişi Sayısı
              </label>
              <select
                value={guests}
                onChange={(e) => { setGuests(e.target.value); setError(''); }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all appearance-none cursor-pointer"
                style={{ backgroundImage: 'none' }}
              >
                {[1,2,3,4,5,6,7,8].map((n) => (
                  <option key={n} value={n}>{n} Kişi</option>
                ))}
                <option value="9+">9+ Kişi</option>
              </select>
            </div>

            {/* Room Type */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Oda Tercihi
              </label>
              <select
                value={room}
                onChange={(e) => { setRoom(e.target.value); setError(''); }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all appearance-none cursor-pointer"
                style={{ backgroundImage: 'none' }}
              >
                <option value="">Oda seçin</option>
                <option value="1+1 Mutfaklı Aile Odası">1+1 Aile Odası</option>
                <option value="Standart Oda">Standart Oda</option>
                <option value="Emin Değilim, Bilgi Almak İstiyorum">Emin Değilim</option>
              </select>
            </div>

            {/* Button */}
            <div className="flex flex-col gap-1.5 justify-end">
              <button
                onClick={handleWhatsApp}
                className="w-full px-4 py-3 bg-primary-500 text-background-50 rounded-xl font-semibold text-sm hover:bg-primary-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <i className="ri-whatsapp-line text-base" />
                Fiyat Al
              </button>
            </div>
          </div>

          {error && (
            <p className="mt-3 text-xs text-red-500 font-medium">{error}</p>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/60 text-xs font-medium">Aşağı kaydır</span>
        <i className="ri-arrow-down-line text-white/60 text-lg" />
      </div>
    </section>
  );
}